import { Col, Row, Space, Switch, Tag, Typography } from 'antd';
import {
  Assert,
  PersonalizationList,
  useExpandItemsToggleSwitch,
  useIsFinalized,
} from '@/views';
import { CommunityPermissions, ProjectStepLookupDto } from '@/core';
import {
  FloorplanPersonalizationNeededSwitch,
  useFloorplanPersonalizationNeededValue,
} from './FloorplanPersonalizationNeededSwitch';
import {
  useDelete,
  useUpdateFloorplanPersonalizationNeededState,
  useStepDeadlinePassed,
} from './FloorplanPersonalizationPanel';
import classNames from 'classnames';
import styles from './PersonalizationListItem.module.scss';
import { useTranslation } from 'react-i18next';

interface StepPersonalizationListProps {
  itemManagementRestricted: boolean;
  homeId: string;
  communityId: string;
  projectId: string;
  step?: ProjectStepLookupDto;
  compact: boolean | undefined;
  readonly: boolean;
}

const { Text } = Typography;

export function StepPersonalizationList(props: StepPersonalizationListProps) {
  const { homeId, projectId, step, compact, readonly } = props;
  const { id: stepId } = step ?? {};

  const handleDelete = useDelete();
  const handleUpdatePersonalizationNeeded =
    useUpdateFloorplanPersonalizationNeededState(props);

  const checkedValue = useFloorplanPersonalizationNeededValue(
    homeId,
    projectId,
    stepId,
  );

  const isHomeFinalized = useIsFinalized(homeId, projectId);
  const stepDeadlinePassed = useStepDeadlinePassed(homeId, projectId, step);
  const isStepFinalized = useIsFinalized(homeId, projectId, stepId);

  const personalizationNeededSwitchVisible =
    (step && !isStepFinalized) || (!step && !isHomeFinalized);

  const isReadonly =
    readonly ||
    checkedValue === null ||
    checkedValue ||
    checkedValue ||
    stepDeadlinePassed === null ||
    stepDeadlinePassed ||
    !!isStepFinalized;

  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.item.details',
  });

  const toggleSwitch = useExpandItemsToggleSwitch(homeId);
  const { expanded, onToggleSwitch } = toggleSwitch;

  return (
    <Space direction="vertical" size="large" className="w-100 m-4">
      <Row align="middle" justify="space-between">
        {step && (
          <Col span={compact ? 24 : 8}>
            <div className={classNames(styles.step, 'mb-2')}>
              <Tag color="blue">
                <Text>{step.name}</Text>
              </Tag>
            </div>
          </Col>
        )}
        <Space wrap className="mb-2">
          <Assert
            permission={CommunityPermissions.Personalization.Manage}
            active
          >
            <>
              {personalizationNeededSwitchVisible && (
                <FloorplanPersonalizationNeededSwitch
                  homeId={homeId}
                  projectId={projectId}
                  handleUpdatePersonalizationNeeded={
                    handleUpdatePersonalizationNeeded
                  }
                  selectedStep={stepId}
                />
              )}
            </>
          </Assert>
          <Space>
            <Switch checked={!expanded} onChange={onToggleSwitch} />
            {t('collapsed')}
          </Space>
        </Space>
      </Row>
      <PersonalizationList
        readonly={isReadonly}
        homeId={homeId}
        projectId={projectId}
        stepId={stepId}
        onDelete={handleDelete}
        compact={compact}
        {...toggleSwitch}
      />
    </Space>
  );
}
